import { useCallback, useEffect, useMemo, useState } from "react"
import { ResponsiveGridLayout, useContainerWidth, Layout } from "react-grid-layout"

import { useTranslation } from "react-i18next"
import { useBlockBrowserBack } from "@/hooks/router/useBlockBrowserBack"
import { useDashboardStore, widgetsToLayouts } from "@/stores/dashboard"
import { useDashboardQuery, useUpdateDashboardMutation } from "@/hooks/api/useDashboardQuery"
import { createGlobalStore } from "@/stores/global"
import {
  ROW_HEIGHT,
  MARGIN,
  CONTAINER_PADDING,
  BREAKPOINTS,
  COLS,
  WIDGET_PALETTE_ITEMS,
} from "./constants/dashboardDefaults"
import type { WidgetPaletteItem, WidgetType } from "@/types/dashboard"

import DashboardWidgetCard from "./components/DashboardWidgetCard"
import DashboardHeader from "./components/DashboardHeader"
import DashboardRowDivider from "./components/DashboardRowDivider"
import DashboardGridOverlay from "./components/DashboardGridOverlay"

import "react-grid-layout/css/styles.css"
import "./dashboard.scss"

export default function Dashboard() {
  const widgets = useDashboardStore((s) => s.widgets)
  const isEditMode = useDashboardStore((s) => s.isEditMode)
  const updateWidgetLayout = useDashboardStore((s) => s.updateWidgetLayout)
  const insertWidgetAtRow = useDashboardStore((s) => s.insertWidgetAtRow)
  const setWidgets = useDashboardStore((s) => s.setWidgets)

  // API — 대시보드 설정 로드
  const { data: dashboardData, isLoading } = useDashboardQuery()
  const { mutate: updateDashboard } = useUpdateDashboardMutation()
  // 첫 렌더부터 스피너: API 응답이 한 번도 안 왔으면 로딩
  const [hasLoaded, setHasLoaded] = useState(false)
  const showLoading = !hasLoaded || isLoading

  const resetToDefault = useDashboardStore((s) => s.resetToDefault)

  // 편집 중 페이지 이탈 방지 (브라우저 뒤로가기 + TanStack Router 네비게이션)
  useBlockBrowserBack(isEditMode)

  // 언마운트 시 편집 중이면 snapshot 복원 (편집 내용 버림)
  useEffect(() => {
    return () => {
      const { isEditMode: editing, snapshot } = useDashboardStore.getState()
      if (editing && snapshot) {
        useDashboardStore.getState().restoreSnapshot()
      }
    }
  }, [])

  useEffect(() => {
    if (!dashboardData) return
    if (dashboardData.widgets.length > 0) {
      setWidgets(dashboardData.widgets)
    } else {
      // 빈 배열 (새 팀, 첫 진입 등) → 기본 레이아웃으로 초기화
      resetToDefault()
    }
    setHasLoaded(true)
  }, [dashboardData])

  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 })

  const layouts = useMemo(() => ({ lg: widgetsToLayouts(widgets) }), [widgets])

  const [isInteracting, setIsInteracting] = useState(false)

  // 위젯 타입별 현재 인스턴스 수 — maxInstances 비교용
  const instanceCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    widgets.forEach((w) => {
      counts[w.widgetType] = (counts[w.widgetType] ?? 0) + 1
    })
    return counts as Record<WidgetType, number>
  }, [widgets])

  // Row divider 위치 — 위젯들의 y 경계 (top 포함) 기준
  const rowBoundaries = useMemo(() => {
    const ys = new Set<number>([0])
    widgets.forEach((w) => ys.add(w.y + w.h))
    return Array.from(ys).sort((a, b) => a - b)
  }, [widgets])

  const handleInsertWidget = useCallback(
    (item: WidgetPaletteItem, y: number) => {
      insertWidgetAtRow(
        {
          widgetType: item.type,
          w: item.defaultWidth,
          h: item.defaultHeight,
          minW: item.minWidth,
          minH: item.minHeight,
          options: {},
        },
        y,
      )
    },
    [insertWidgetAtRow],
  )

  const handleLayoutChange = useCallback(
    (layout: Layout) => {
      if (!isEditMode) {
        return
      }
      layout.forEach((layoutItem) => {
        const widget = widgets.find((w) => w.id === layoutItem.i)
        if (
          widget &&
          (widget.x !== layoutItem.x ||
            widget.y !== layoutItem.y ||
            widget.w !== layoutItem.w ||
            widget.h !== layoutItem.h)
        ) {
          updateWidgetLayout(layoutItem.i, {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          })
        }
      })
    },
    [isEditMode, widgets, updateWidgetLayout],
  )

  const { t } = useTranslation()
  const setSnackBar = createGlobalStore((s) => s.setSnackBar)

  const handleSave = useCallback(() => {
    updateDashboard(widgets, {
      onSuccess: () => {
        setSnackBar({
          open: true,
          message: t("messages.successUpdate"),
          severity: "success",
        })
      },
      onError: () => {
        setSnackBar({
          open: true,
          message: t("messages.failSave", "Failed to save"),
          severity: "error",
        })
      },
    })
  }, [widgets, updateDashboard, setSnackBar, t])

  return (
    <>
      <div className={`DashboardContainer ${isEditMode ? "DashboardContainer--editing" : ""}`}>
        <DashboardHeader onSave={handleSave} />

        <div className="DashboardContainer__body">
          <div ref={containerRef} className="DashboardContainer__grid">
            {showLoading ? (
              <div className="DashboardContainer__skeleton" />
            ) : (
              mounted && (
                <>
                  <DashboardGridOverlay
                    cols={COLS.lg}
                    rowHeight={ROW_HEIGHT}
                    margin={MARGIN}
                    containerPadding={CONTAINER_PADDING}
                    canvasWidth={width}
                    canvasHeight={800}
                    visible={isEditMode && isInteracting}
                  />
                  <ResponsiveGridLayout
                    width={width}
                    breakpoints={BREAKPOINTS}
                    cols={COLS}
                    rowHeight={ROW_HEIGHT}
                    margin={MARGIN}
                    containerPadding={CONTAINER_PADDING}
                    layouts={layouts}
                    dragConfig={{
                      enabled: isEditMode,
                      handle: ".DashboardWidgetCard__header",
                    }}
                    resizeConfig={{
                      enabled: isEditMode,
                      handles: ["s", "w", "e", "n", "sw", "nw", "se", "ne"],
                    }}
                    onDragStart={() => setIsInteracting(true)}
                    onDragStop={(layout) => {
                      setIsInteracting(false)
                      handleLayoutChange(layout)
                    }}
                    onResizeStart={() => setIsInteracting(true)}
                    onResizeStop={(layout) => {
                      setIsInteracting(false)
                      handleLayoutChange(layout)
                    }}
                    onLayoutChange={handleLayoutChange}>
                    {widgets.map((widget) => (
                      <div key={widget.id}>
                        <DashboardWidgetCard widget={widget} />
                      </div>
                    ))}
                  </ResponsiveGridLayout>

                  {isEditMode &&
                    rowBoundaries.map((y) => (
                      <DashboardRowDivider
                        key={`divider-${y}`}
                        y={y}
                        topPx={y * ROW_HEIGHT + Math.max(y - 1, 0) * MARGIN[1] + MARGIN[1] / 2}
                        paletteItems={WIDGET_PALETTE_ITEMS}
                        instanceCounts={instanceCounts}
                        onAdd={handleInsertWidget}
                      />
                    ))}
                </>
              )
            )}
          </div>
        </div>
      </div>
    </>
  )
}
